/**
 * LE POSTE CLIENT ET LES ENCAISSEMENTS LUS CHEZ PENNYLANE — lot 158, 03/11/2026.
 *
 * Les types des deux routes `GET /recouvrement-pl/poste` et
 * `GET /recouvrement-pl/encaissements` (`apps/api/src/recouvrement_pl.ts`), et
 * les calculs purs des deux écrans : recherche, chiffres, écart à l'échéance.
 */

import type { NiveauPl } from './relancesPl.js';

export interface FactureDuPoste {
  readonly pennylane_facture_id: string;
  readonly numero: string | null;
  readonly echeance: string | null;
  readonly retard_jours: number;
  readonly reste_du: string;
  readonly echue: boolean;
  readonly derniere: { readonly niveau: NiveauPl; readonly envoyee_le: string } | null;
  readonly a_envoyer: NiveauPl | null;
  readonly gel: { readonly motif: string; readonly reprise_le: string } | null;
}

export interface ClientDuPoste {
  readonly cle: string;
  readonly client_id: string | null;
  readonly nom: string;
  readonly factures: readonly FactureDuPoste[];
  readonly total_centimes: number;
  readonly echu_centimes: number;
  readonly plus_ancienne: string | null;
  readonly derniere: { readonly niveau: NiveauPl; readonly envoyee_le: string } | null;
}

export interface Poste {
  readonly jour: string;
  readonly clients: readonly ClientDuPoste[];
}

export interface Encaissement {
  readonly pennylane_facture_id: string;
  readonly numero: string | null;
  readonly client: string;
  readonly echeance: string | null;
  readonly reglee_le: string;
  readonly montant_ttc: string;
  readonly ecart_jours: number | null;
}

export interface Encaissements {
  readonly du: string;
  readonly au: string;
  /** Factures payées dont Pennylane n'a pas encore donné la date de règlement. */
  readonly sans_date: number;
  readonly encaissements: readonly Encaissement[];
}

/** Sans accent ni majuscule, pour comparer ce qu'on tape à ce qu'on lit. */
function plat(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** La recherche porte sur le nom du client et les numéros de ses factures. */
export function filtrerPoste(
  clients: readonly ClientDuPoste[],
  recherche: string,
  seulementEnRetard: boolean,
): readonly ClientDuPoste[] {
  const q = plat(recherche.trim());
  return clients.filter(
    (c) =>
      (!seulementEnRetard || c.echu_centimes > 0) &&
      (q === '' || plat(c.nom).includes(q) || c.factures.some((f) => plat(f.numero ?? '').includes(q))),
  );
}

/** Les trois tuiles du poste — sur tout le poste, pas seulement ce qu'on voit. */
export function chiffresPoste(clients: readonly ClientDuPoste[]) {
  return {
    total_centimes: clients.reduce((s, c) => s + c.total_centimes, 0),
    echu_centimes: clients.reduce((s, c) => s + c.echu_centimes, 0),
    en_retard: clients.filter((c) => c.echu_centimes > 0).length,
    clients: clients.length,
  };
}

export function filtrerEncaissements(
  lignes: readonly Encaissement[],
  recherche: string,
): readonly Encaissement[] {
  const q = plat(recherche.trim());
  if (q === '') return lignes;
  return lignes.filter((l) => plat(l.client).includes(q) || plat(l.numero ?? '').includes(q));
}

/**
 * Les trois tuiles des encaissements. LE RETARD MOYEN est la moyenne simple de
 * l'écart à l'échéance, avance comprise (un jour d'avance compte −1) ; les
 * factures sans échéance n'y entrent pas.
 */
export function chiffresEncaissements(lignes: readonly Encaissement[]) {
  const ecarts = lignes.map((l) => l.ecart_jours).filter((e): e is number => e !== null);
  return {
    encaisse_centimes: lignes.reduce((s, l) => s + Math.round(Number(l.montant_ttc) * 100), 0),
    reglees: lignes.length,
    retard_moyen: ecarts.length === 0 ? null : Math.round(ecarts.reduce((s, e) => s + e, 0) / ecarts.length),
  };
}

/** « +18 j », « à l'échéance », « 1 j d'avance », et la couleur qui va avec. */
export function ecartDit(ecart: number | null): { readonly texte: string; readonly ton: 'vert' | 'ambre' | 'rouge' | 'gris' } {
  if (ecart === null) return { texte: '—', ton: 'gris' };
  if (ecart === 0) return { texte: 'à l’échéance', ton: 'vert' };
  if (ecart < 0) return { texte: `${String(-ecart)} j d’avance`, ton: 'vert' };
  return { texte: `+${String(ecart)} j`, ton: ecart > 30 ? 'rouge' : 'ambre' };
}

/** La période proposée à l'ouverture : du 1er du mois précédent à aujourd'hui. */
export function periodeParDefaut(jour: string): { readonly du: string; readonly au: string } {
  const annee = Number(jour.slice(0, 4));
  const mois = Number(jour.slice(5, 7));
  const a = mois === 1 ? annee - 1 : annee;
  const m = mois === 1 ? 12 : mois - 1;
  return { du: `${String(a)}-${String(m).padStart(2, '0')}-01`, au: jour };
}

// ---------------------------------------------------------------------------
// LE SUIVI — lot 159, 03/11/2026 (planche « Recouvrement · 6 », validée le 03/11).

export interface TrancheAgee {
  readonly nom: string;
  readonly centimes: number;
}

/**
 * LA BALANCE ÂGÉE : le reste dû de chaque facture ouverte, rangé par jours de
 * retard. Les six tranches de l'ancien poste client (étape 16) : à échoir,
 * 1-15, 16-30, 31-45, 46-60, plus de 60.
 */
export function balanceAgeePl(clients: readonly ClientDuPoste[]): readonly TrancheAgee[] {
  const bornes: readonly [string, number][] = [
    ['À échoir', 0],
    ['1 à 15 j', 15],
    ['16 à 30 j', 30],
    ['31 à 45 j', 45],
    ['46 à 60 j', 60],
    ['Plus de 60 j', Number.POSITIVE_INFINITY],
  ];
  const sommes = bornes.map(() => 0);
  for (const c of clients) {
    for (const f of c.factures) {
      const r = f.echue ? Math.max(1, f.retard_jours) : 0;
      const i = bornes.findIndex(([, max]) => r <= max);
      sommes[i] = (sommes[i] ?? 0) + Math.round(Number(f.reste_du) * 100);
    }
  }
  return bornes.map(([nom], i) => ({ nom, centimes: sommes[i] ?? 0 }));
}

/** Le plus gros retard parmi les factures échues d'un client, en jours. */
export function plusGrosRetard(c: ClientDuPoste): number {
  return c.factures.reduce((m, f) => (f.echue ? Math.max(m, f.retard_jours) : m), 0);
}

/** Les `n` clients qui doivent le plus d'échu. */
export function lesPlusEchus(clients: readonly ClientDuPoste[], n: number): readonly ClientDuPoste[] {
  return [...clients]
    .filter((c) => c.echu_centimes > 0)
    .sort((a, b) => b.echu_centimes - a.echu_centimes)
    .slice(0, n);
}

/** Les encaissements dont la date de règlement est entre `du` et `au` inclus. */
export function encaissementsEntre(
  lignes: readonly Encaissement[],
  du: string,
  au: string,
): readonly Encaissement[] {
  return lignes.filter((l) => l.reglee_le >= du && l.reglee_le <= au);
}
